/**
 * Document Manager Sheet
 * Admin-only view for managing RAG documents
 * Upload, view status, and delete documents
 */

import { useState, useRef, useCallback } from 'react'
import {
  FileText,
  FileSpreadsheet,
  Upload,
  Trash2,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  File,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useDocumentsWithPolling,
  useUploadDocument,
  useDeleteDocument,
} from '@/hooks'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import type { Document, DocumentType } from '@/lib/repositories'

// Allowed file extensions
const ACCEPTED_TYPES = '.csv,.xlsx,.xls,.pdf,.docx,.txt'
const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

interface DocumentManagerSheetProps {
  open: boolean
  onClose: () => void
}

export function DocumentManagerSheet({
  open,
  onClose,
}: DocumentManagerSheetProps) {
  const { data: documents = [], isLoading } = useDocumentsWithPolling()
  const uploadMutation = useUploadDocument()
  const deleteMutation = useDeleteDocument()

  const [documentType, setDocumentType] = useState<DocumentType>('ticker')
  const [ticker, setTicker] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const tickerTrimmed = ticker.trim()
  const hasValidTicker = tickerTrimmed.length >= 1
  const canUpload = documentType === 'strategy' || hasValidTicker

  // Upload handler
  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      if (!canUpload) {
        toast.error(
          documentType === 'ticker'
            ? 'Ticker is required before uploading'
            : 'Unable to upload'
        )
        return
      }

      const fileArray = Array.from(files)

      for (const file of fileArray) {
        if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
          toast.error(
            `Unsupported file: ${file.name}. Allowed: CSV, XLSX, PDF, DOCX`
          )
          continue
        }

        if (file.size > 50 * 1024 * 1024) {
          toast.error(`File too large: ${file.name}. Max 25MB`)
          continue
        }

        try {
          await uploadMutation.mutateAsync({
            file,
            ticker: documentType === 'ticker' ? tickerTrimmed : undefined,
            documentType,
          })
          toast.success(`Uploaded: ${file.name}`)
        } catch (error) {
          toast.error(
            `Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [uploadMutation, tickerTrimmed, canUpload, documentType]
  )

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files)
      }
    },
    [handleUpload]
  )

  // Delete handler
  const handleDelete = async (doc: Document) => {
    if (
      !confirm(
        `Delete "${doc.filename}"? This will also remove all vectorized data.`
      )
    )
      return

    try {
      await deleteMutation.mutateAsync(doc.id)
      toast.success(`Deleted: ${doc.filename}`)
    } catch (error) {
      toast.error(
        `Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // File type icon
  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'csv':
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-5 h-5" />
      case 'pdf':
        return <FileText className="w-5 h-5" />
      case 'docx':
        return <File className="w-5 h-5" />
      default:
        return <File className="w-5 h-5" />
    }
  }

  // Status badge
  const getStatusBadge = (status: string, errorMessage?: string | null) => {
    switch (status) {
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            Processing
          </span>
        )
      case 'ready':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3 h-3" />
            Ready
          </span>
        )
      case 'failed':
        return (
          <span
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20"
            title={errorMessage || 'Processing failed'}
          >
            <AlertCircle className="w-3 h-3" />
            Failed
          </span>
        )
      default:
        return null
    }
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Format date
  const formatDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const processingCount = documents.filter(
    (d) => d.status === 'processing'
  ).length
  const readyCount = documents.filter((d) => d.status === 'ready').length

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="h-[97vh]">
        <SheetHeader className="flex flex-row items-center justify-between">
          <SheetTitle className="text-white text-lg font-semibold">
            📚 Document Manager
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        <div className="px-4 pb-6 flex-1 overflow-y-auto space-y-6">
          {/* Upload Section */}
          <div className="space-y-3">
            {/* Document Type Selector */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-neutral-400">
                Document Type
              </label>
              <div className="flex rounded-lg overflow-hidden border border-white/[0.08]">
                <button
                  type="button"
                  onClick={() => setDocumentType('ticker')}
                  className={cn(
                    'flex-1 px-3 py-2 text-xs font-medium transition-colors',
                    documentType === 'ticker'
                      ? 'bg-violet-500/20 text-violet-300 border-r border-violet-500/30'
                      : 'bg-white/[0.02] text-neutral-400 border-r border-white/[0.08] hover:bg-white/[0.04]'
                  )}
                >
                  Ticker-Specific
                </button>
                <button
                  type="button"
                  onClick={() => setDocumentType('strategy')}
                  className={cn(
                    'flex-1 px-3 py-2 text-xs font-medium transition-colors',
                    documentType === 'strategy'
                      ? 'bg-blue-500/20 text-blue-300'
                      : 'bg-white/[0.02] text-neutral-400 hover:bg-white/[0.04]'
                  )}
                >
                  General Strategy
                </button>
              </div>
            </div>

            {/* Ticker Input (Required for ticker-specific documents) */}
            {documentType === 'ticker' && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-neutral-400">
                  Ticker <span className="text-red-400">*</span>
                </label>
                <Input
                  placeholder="e.g. AAPL, NVDA, GOOGL"
                  value={ticker}
                  onChange={(e) =>
                    setTicker(
                      e.target.value.toUpperCase().replace(/[^A-Z]/g, '')
                    )
                  }
                  maxLength={10}
                  className={cn(
                    'bg-white/[0.04] border text-white placeholder:text-neutral-500 text-sm font-mono tracking-wider',
                    hasValidTicker
                      ? 'border-emerald-500/30 focus:border-emerald-500/50'
                      : 'border-white/[0.06]'
                  )}
                />
                {!hasValidTicker && (
                  <p className="text-[11px] text-neutral-500">
                    Assign a ticker to link this data to AI insights
                  </p>
                )}
              </div>
            )}

            {documentType === 'strategy' && (
              <p className="text-[11px] text-blue-400/70">
                Strategy documents are available across all AI chats for
                questions about methodology, stock selection, and trading
                approach.
              </p>
            )}

            {/* Drop Zone */}
            <div
              className={cn(
                'relative border-2 border-dashed rounded-xl p-6 text-center transition-all',
                !canUpload && 'opacity-40 pointer-events-none',
                canUpload && isDragging
                  ? 'border-emerald-500/50 bg-emerald-500/5'
                  : canUpload
                    ? 'border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02] cursor-pointer'
                    : 'border-white/[0.06]',
                uploadMutation.isPending && 'pointer-events-none opacity-60'
              )}
              onDragOver={canUpload ? handleDragOver : undefined}
              onDragLeave={canUpload ? handleDragLeave : undefined}
              onDrop={canUpload ? handleDrop : undefined}
              onClick={() => canUpload && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                className="hidden"
                disabled={!canUpload}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleUpload(e.target.files)
                  }
                }}
              />

              {uploadMutation.isPending ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                  <p className="text-sm text-neutral-400">
                    Uploading
                    {documentType === 'ticker' && (
                      <>
                        {' '}
                        to{' '}
                        <span className="font-mono text-emerald-400">
                          {tickerTrimmed}
                        </span>
                      </>
                    )}
                    {documentType === 'strategy' && (
                      <span className="text-blue-400"> strategy document</span>
                    )}
                    ...
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload
                    className={cn(
                      'w-8 h-8 transition-colors',
                      isDragging ? 'text-emerald-400' : 'text-neutral-500'
                    )}
                  />
                  {canUpload ? (
                    <>
                      <p className="text-sm text-neutral-300">
                        Drop files here or{' '}
                        <span className="text-emerald-400">browse</span>
                      </p>
                      <p className="text-xs text-neutral-500">
                        CSV, XLSX, PDF, DOCX — Max 25MB
                        {documentType === 'ticker' && (
                          <>
                            {' '}
                            → tagged as{' '}
                            <span className="font-mono text-emerald-400">
                              {tickerTrimmed}
                            </span>
                          </>
                        )}
                        {documentType === 'strategy' && (
                          <>
                            {' '}
                            →{' '}
                            <span className="text-blue-400">
                              general strategy
                            </span>
                          </>
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-neutral-500">
                      Enter a ticker above to enable upload
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats Bar */}
          {documents.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-neutral-400">
              <span>
                {documents.length} document{documents.length !== 1 ? 's' : ''}
              </span>
              {readyCount > 0 && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  {readyCount} ready
                </span>
              )}
              {processingCount > 0 && (
                <span className="flex items-center gap-1 text-amber-400">
                  <Clock className="w-3 h-3" />
                  {processingCount} processing
                </span>
              )}
            </div>
          )}

          {/* Documents List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-neutral-500 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-neutral-400 text-sm">
                No documents uploaded yet
              </p>
              <p className="text-neutral-500 text-xs mt-1">
                Upload backtested data to enhance AI responses
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  getFileIcon={getFileIcon}
                  getStatusBadge={getStatusBadge}
                  formatFileSize={formatFileSize}
                  formatDate={formatDate}
                  onDelete={handleDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ---- Document Row Component ----

interface DocumentRowProps {
  doc: Document
  getFileIcon: (fileType: string) => React.ReactNode
  getStatusBadge: (
    status: string,
    errorMessage?: string | null
  ) => React.ReactNode
  formatFileSize: (bytes: number) => string
  formatDate: (date: Date) => string
  onDelete: (doc: Document) => void
  isDeleting: boolean
}

function DocumentRow({
  doc,
  getFileIcon,
  getStatusBadge,
  formatFileSize,
  formatDate,
  onDelete,
  isDeleting,
}: DocumentRowProps) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors">
      {/* File Icon */}
      <div
        className={cn(
          'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
          doc.fileType === 'csv' ||
            doc.fileType === 'xlsx' ||
            doc.fileType === 'xls'
            ? 'bg-emerald-500/10 text-emerald-400'
            : doc.fileType === 'pdf'
              ? 'bg-red-500/10 text-red-400'
              : 'bg-blue-500/10 text-blue-400'
        )}
      >
        {getFileIcon(doc.fileType)}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm text-white font-medium truncate">
            {doc.filename}
          </p>
          {doc.documentType === 'strategy' ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Strategy
            </span>
          ) : doc.ticker ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 border border-violet-500/20 font-mono">
              {doc.ticker}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2 mt-1">
          {getStatusBadge(doc.status, doc.errorMessage)}
          <span className="text-xs text-neutral-500">
            {formatFileSize(doc.fileSize)}
          </span>
          {doc.chunkCount > 0 && (
            <span className="text-xs text-neutral-500">
              {doc.chunkCount} chunks
            </span>
          )}
          <span className="text-xs text-neutral-600">
            {formatDate(doc.createdAt)}
          </span>
        </div>

        {/* Error message */}
        {doc.status === 'failed' && doc.errorMessage && (
          <p
            className="text-xs text-red-400/80 mt-1 truncate"
            title={doc.errorMessage}
          >
            {doc.errorMessage}
          </p>
        )}
      </div>

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(doc)}
        disabled={isDeleting}
        className="flex-shrink-0 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  )
}
